import React, { FC, useState } from 'react'

import { createCookie, getCookie } from '@smarty-nx/code-utils'
import dayjs from 'dayjs'
import { useHistory } from 'react-router-dom'

import { content } from './customer-raf-modal.content'
import classes from './customer-raf-modal.module.scss'
import rafBannerImage from './raf-banner.png'
import { Modal, Typography, TextInput, Button, Level } from '../../../index'

interface CustomerRafModalProps {
  userId: string | undefined
  buyapowaShareUrl: string
  referFriendUrl: string
}

export const CustomerRafModal: FC<CustomerRafModalProps> = ({
  userId,
  buyapowaShareUrl,
  referFriendUrl,
}) => {
  const history = useHistory()

  const [showModal, setShowModal] = useState(true)

  // Modal should appear no more than 1 time per session and no more than 3 times in a 24 hour period
  const shouldShowModal = () => {
    const rafModalDate = getCookie({ name: `${userId}-rafModalDate` })
    const rafModalCount = getCookie({ name: `${userId}-rafModalCount` })

    // Modal already shown in current session
    if (window.localStorage.getItem('rafModalShown') === 'true') {
      return false
    }

    // We show modal for the first time
    if (!rafModalDate || !rafModalCount) {
      return true
    }

    const showWithin24Period =
      dayjs(dayjs()).diff(rafModalDate, 'hours') < 24 &&
      Number(rafModalCount) < 3

    const new24PeriodStarted = dayjs(dayjs()).diff(rafModalDate, 'hours') > 24

    return showWithin24Period || new24PeriodStarted
  }

  const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    navigator.clipboard.writeText(buyapowaShareUrl).catch((error) => {
      console.error(error)
    })
    e.currentTarget.blur()
  }

  const handleClose = () => {
    setShowModal(false)
    window.localStorage.setItem('rafModalShown', 'true')

    const rafModalDate = getCookie({ name: `${userId}-rafModalDate` })
    const rafModalCount = getCookie({ name: `${userId}-rafModalCount` })

    // When modal is shown for the first time or 24 hour period passed, we set new date and reset count to 1
    if (
      !rafModalDate ||
      !rafModalCount ||
      dayjs(dayjs()).diff(rafModalDate, 'hours') > 24
    ) {
      createCookie({
        name: `${userId}-rafModalDate`,
        value: dayjs().toString(),
        days: 1,
      })
      createCookie({
        name: `${userId}-rafModalCount`,
        value: String(1),
        days: 1,
      })
    }
    // We increment count up to 3 because modal should be shown no more than 3 times
    else if (
      Number(rafModalCount) < 3 &&
      dayjs(dayjs()).diff(rafModalDate, 'hours') < 24
    ) {
      const updatedCount = Number(rafModalCount) + 1
      createCookie({
        name: `${userId}-rafModalCount`,
        value: updatedCount.toString(),
        days: 1,
      })
    }
  }

  const handleNavigate = () => {
    handleClose()
    history.push(referFriendUrl)
  }

  if (!showModal || !shouldShowModal()) {
    return null
  }

  return (
    <Modal
      isOpen={showModal}
      onRequestClose={handleClose}
      testId="customer-raf-modal"
      modalClassName={classes.modalContainer}
      closeButtonClassName={classes.closeButton}
    >
      <div className={classes.root}>
        <div className={classes.modalHeader}>
          <img className={classes.rafBanner} src={rafBannerImage} alt="" />
        </div>
        <div className={classes.modalBody}>
          <Typography
            variant="Kollektif/Large"
            component="h2"
            dataTestId="customer-raf-title"
          >
            {content.title}
          </Typography>
          <Typography
            variant="Kollektif/Medium"
            dataTestId="customer-raf-subtitle"
          >
            {content.subTitle}
          </Typography>
          <Typography
            variant="Kollektif/Small"
            dataTestId="customer-raf-description"
          >
            {content.description}
          </Typography>
          <div className={classes.copyUrlWrapper}>
            <TextInput
              labelContent={''}
              onReset={() => null}
              type="text"
              value={buyapowaShareUrl}
              descriptionDataTestId="customer-raf-description"
              descriptionClassName={classes.noDescription}
              wrapperClassName={classes.inputWrapper}
              readOnly
              showResetButton={false}
              dataTestId="customer-raf-input"
            />
            <div className={classes.desktop}>
              <Button
                className={classes.copyButton}
                onClick={handleCopy}
                data-testid="customer-raf-copy-button"
                level={Level.Quaternary}
              >
                {content.copyCta}
              </Button>
            </div>
          </div>
          <Typography
            variant="Kollektif/Tiny"
            dataTestId="customer-raf-info-text"
          >
            {content.infoText}
          </Typography>
          <div className={classes.mobile}>
            <Button
              className={classes.copyButton}
              onClick={handleCopy}
              data-testid="customer-raf-copy-button"
              level={Level.Quaternary}
            >
              {content.copyCta}
            </Button>
          </div>
          <div className={classes.referNowButton}>
            <Button
              dataTestId="refer-now-button"
              size="regular"
              onClick={handleNavigate}
            >
              {content.referCta}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
